import { useEffect, useState } from 'react';

import { DEFAULT_COUNTRY_CODE } from '../constants/common';

const API_URLS = ['https://ipapi.co/json/', 'https://ipinfo.io/json'];

const CACHE_KEY = 'userCountry';
const CACHE_TIME_KEY = 'userCountryTimestamp';
const ONE_DAY = 24 * 60 * 60 * 1000;

async function fetchCountryWithFallback(retry = false) {
  const controllers = API_URLS.map(() => new AbortController());

  const requests = API_URLS.map((url, i) =>
    fetch(url, { signal: controllers[i].signal })
      .then(res => {
        if (!res.ok) throw new Error(`Bad response: ${url}`);
        return res.json();
      })
      .then(data => ({ success: true, data, i }))
      .catch(() => ({ success: false, i }))
  );

  try {
    const { data, i } = await Promise.any(
      requests.map(req =>
        req.then(result => {
          if (result.success) {
            controllers.forEach((c, j) => j !== result.i && c.abort());
            return result;
          }
          throw new Error('Failed');
        })
      )
    );

    const countryCode = data.country || data.country_code || DEFAULT_COUNTRY_CODE;
    const address = { countryCode: countryCode?.toLowerCase(), city: data?.city };

    localStorage.setItem(CACHE_KEY, JSON.stringify(address));
    localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());

    return address;
  } catch (err) {
    if (!retry) {
      console.warn('Retrying country fetch...');
      await new Promise(res => setTimeout(res, 500));
      return fetchCountryWithFallback(true);
    }
    console.error('All country APIs failed', err);
    return 'Unknown';
  }
}

export function useUserCountry() {
  const [country, setCountry] = useState(null);

  useEffect(() => {
    async function loadCountry() {
      const cached = localStorage.getItem(CACHE_KEY);
      const cachedTime = localStorage.getItem(CACHE_TIME_KEY);

      if (cached && cachedTime && Date.now() - +cachedTime < ONE_DAY) {
        try {
          const parsedData = JSON.parse(cached);
          setCountry(parsedData);
        } catch (error) {
          console.error(error);
        }

        return;
      }

      const result = await fetchCountryWithFallback();
      setCountry(result);
    }

    loadCountry();
  }, []);

  return country;
}
